import { supabase, callEdgeFunction } from './supabase';
import type { AdminAuthResponse, ModerationCounts, UserLoginEvent, UserLoginEventFilters } from '@/types/admin';
import type { UserWithStatus, TosViolation } from '@/types/user';
import type { BusinessWithSubscription, BusinessModerationQueue } from '@/types/business';
import type { ConversationFlag, ReviewFlag } from '@/types/moderation';
import type { PaymentRecord } from '@/types/payment';
import type { PlatformMetricsDaily, DashboardStats } from '@/types/analytics';

// Admin Authentication
export async function getAdminAuth(): Promise<AdminAuthResponse> {
  return callEdgeFunction<AdminAuthResponse>('admin-auth', {});
}

// Dashboard Stats
export async function getDashboardStats(): Promise<DashboardStats> {
  const today = new Date().toISOString().split('T')[0];

  const [
    usersResult,
    businessesResult,
    activeUsersResult,
    metricsResult,
    moderationResult,
    flagsResult,
  ] = await Promise.all([
    supabase.from('user_profiles').select('*', { count: 'exact', head: true }),
    supabase.from('business_profiles').select('*', { count: 'exact', head: true }),
    supabase.from('user_profiles').select('*', { count: 'exact', head: true })
      .gte('updated_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()),
    supabase.from('platform_metrics_daily').select('*').eq('date', today).maybeSingle(),
    supabase.from('business_moderation_queue').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
    supabase.from('conversation_flags').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
  ]);

  // Get new users/businesses today
  const startOfDay = `${today}T00:00:00.000Z`;
  const [newUsersResult, newBusinessesResult] = await Promise.all([
    supabase.from('user_profiles').select('*', { count: 'exact', head: true }).gte('created_at', startOfDay),
    supabase.from('business_profiles').select('*', { count: 'exact', head: true }).gte('created_at', startOfDay),
  ]);

  return {
    totalUsers: usersResult.count || 0,
    totalBusinesses: businessesResult.count || 0,
    activeUsers: activeUsersResult.count || 0,
    mrr: (metricsResult.data?.mrr_cents || 0) / 100,
    newUsersToday: newUsersResult.count || 0,
    newBusinessesToday: newBusinessesResult.count || 0,
    pendingModeration: moderationResult.count || 0,
    pendingFlags: flagsResult.count || 0,
  };
}

// Users
export async function getUsers(
  page = 1,
  pageSize = 20,
  search?: string,
  status?: string
): Promise<{ users: UserWithStatus[]; total: number }> {
  let query = supabase
    .from('user_profiles')
    .select('*, user_account_status(*)', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (search) {
    query = query.or(`full_name.ilike.%${search}%,email.ilike.%${search}%,phone_number.ilike.%${search}%`);
  }

  const { data, count, error } = await query;
  if (error) throw error;

  // Map account status
  const users: UserWithStatus[] = (data || []).map(user => ({
    ...user,
    account_status: user.user_account_status?.[0] || null,
  }));

  // Filter by status if specified
  let filteredUsers = users;
  if (status && status !== 'all') {
    filteredUsers = users.filter(u => {
      const accountStatus = u.account_status?.status || 'active';
      return accountStatus === status;
    });
  }

  return { users: filteredUsers, total: count || 0 };
}

export async function getUserById(userId: string): Promise<UserWithStatus | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*, user_account_status(*)')
    .eq('user_id', userId)
    .single();

  if (error) throw error;

  return data ? {
    ...data,
    account_status: data.user_account_status?.[0] || null,
  } : null;
}

export async function getUserViolations(userId: string): Promise<TosViolation[]> {
  const { data, error } = await supabase
    .from('tos_violations')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Businesses
export async function getBusinesses(
  page = 1,
  pageSize = 20,
  search?: string,
  tier?: string
): Promise<{ businesses: BusinessWithSubscription[]; total: number }> {
  let query = supabase
    .from('business_profiles')
    .select('*, business_subscriptions(*), owner:user_profiles!owner_id(full_name, email)', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (search) {
    query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%`);
  }

  if (tier && tier !== 'all') {
    query = query.eq('tier', tier);
  }

  const { data, count, error } = await query;
  if (error) throw error;

  const businesses: BusinessWithSubscription[] = (data || []).map(biz => ({
    ...biz,
    subscription: biz.business_subscriptions?.[0] || null,
  }));

  return { businesses, total: count || 0 };
}

export async function getModerationQueue(): Promise<BusinessModerationQueue[]> {
  const { data, error } = await supabase
    .from('business_moderation_queue')
    .select('*, business_profiles(*)')
    .eq('status', 'pending')
    .order('priority', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

// Moderation
export async function getModerationCounts(): Promise<ModerationCounts> {
  const [convFlags, reviewFlags, bizModeration] = await Promise.all([
    supabase.from('conversation_flags').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
    supabase.from('review_flags').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
    supabase.from('business_moderation_queue').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
  ]);

  return {
    pending_conversation_flags: convFlags.count || 0,
    pending_review_flags: reviewFlags.count || 0,
    pending_business_moderation: bizModeration.count || 0,
  };
}

export async function getConversationFlags(
  status = 'pending',
  page = 1,
  pageSize = 20
): Promise<{ flags: ConversationFlag[]; total: number }> {
  let query = supabase
    .from('conversation_flags')
    .select(`
      *,
      reporter:user_profiles!flagged_by(full_name, email),
      flagged_user:user_profiles!flagged_user_id(full_name, email)
    `, { count: 'exact' })
    .order('priority', { ascending: false })
    .order('created_at', { ascending: true })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (status !== 'all') {
    query = query.eq('status', status);
  }

  const { data, count, error } = await query;
  if (error) throw error;

  return { flags: data || [], total: count || 0 };
}

export async function getReviewFlags(
  status = 'pending',
  page = 1,
  pageSize = 20
): Promise<{ flags: ReviewFlag[]; total: number }> {
  let query = supabase
    .from('review_flags')
    .select(`
      *,
      business:business_profiles!business_id(name)
    `, { count: 'exact' })
    .order('priority', { ascending: false })
    .order('created_at', { ascending: true })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (status !== 'all') {
    query = query.eq('status', status);
  }

  const { data, count, error } = await query;
  if (error) throw error;

  return { flags: data || [], total: count || 0 };
}

// Payments
export async function getPayments(
  page = 1,
  pageSize = 20,
  status?: string
): Promise<{ payments: PaymentRecord[]; total: number }> {
  let query = supabase
    .from('payment_records')
    .select('*, business:business_profiles!business_id(name, owner_id)', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (status && status !== 'all') {
    query = query.eq('status', status);
  }

  const { data, count, error } = await query;
  if (error) throw error;

  return { payments: data || [], total: count || 0 };
}

// Analytics
export async function getMetrics(
  startDate: string,
  endDate: string
): Promise<PlatformMetricsDaily[]> {
  const { data, error } = await supabase
    .from('platform_metrics_daily')
    .select('*')
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: true });

  if (error) throw error;
  return data || [];
}

// Access Codes
export async function getAccessCodes(page = 1, pageSize = 20) {
  // Fetch access codes with usage information
  // Note: We fetch user profiles, business profiles, and access_code_requests separately
  // to avoid RLS issues with nested joins
  const { data, count, error } = await supabase
    .from('access_codes')
    .select(`
      *,
      access_code_usage (
        user_id,
        used_at
      )
    `, { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (error) throw error;

  // Get access code IDs to fetch related requests
  const accessCodeIds = (data || []).map(code => code.id);

  // Get all unique user IDs that used codes to fetch their profiles and business profiles
  const userIds = (data || [])
    .flatMap(code => code.access_code_usage || [])
    .map(usage => usage.user_id)
    .filter((id): id is string => id !== null);

  let userByUserId: Record<string, { user_id: string; full_name: string; user_email: string; user_phone_number: string } | null> = {};
  let businessByUserId: Record<string, { business_id: string; business_name: string; contact_email: string; phone: string; business_status: string } | null> = {};
  let requestByCodeId: Record<string, { email: string | null; phone: string | null; city: string | null; state: string | null; signup_intent: string | null } | null> = {};

  // Fetch access_code_requests separately to avoid RLS issues with nested joins
  if (accessCodeIds.length > 0) {
    const { data: requests } = await supabase
      .from('access_code_requests')
      .select('access_code_id, email, phone, city, state, signup_intent')
      .in('access_code_id', accessCodeIds);

    if (requests) {
      requestByCodeId = requests.reduce((acc, req) => {
        acc[req.access_code_id] = {
          email: req.email,
          phone: req.phone,
          city: req.city,
          state: req.state,
          signup_intent: req.signup_intent
        };
        return acc;
      }, {} as typeof requestByCodeId);
    }
  }

  if (userIds.length > 0) {
    // Fetch user profiles and business profiles in parallel
    const [usersResult, businessesResult] = await Promise.all([
      supabase
        .from('user_profiles')
        .select('user_id, full_name, user_email, user_phone_number')
        .in('user_id', userIds),
      supabase
        .from('business_profiles')
        .select('user_id, business_id, business_name, contact_email, phone, business_status')
        .in('user_id', userIds)
    ]);

    if (usersResult.data) {
      userByUserId = usersResult.data.reduce((acc, user) => {
        acc[user.user_id] = user;
        return acc;
      }, {} as typeof userByUserId);
    }

    if (businessesResult.data) {
      businessByUserId = businessesResult.data.reduce((acc, biz) => {
        acc[biz.user_id] = biz;
        return acc;
      }, {} as typeof businessByUserId);
    }
  }

  // Enrich codes with recipient, business info, and request info
  const enrichedCodes = (data || []).map(code => {
    const usage = code.access_code_usage?.[0];
    const userId = usage?.user_id;
    const user = userId ? userByUserId[userId] : null;
    const business = userId ? businessByUserId[userId] : null;
    const request = requestByCodeId[code.id] || null;

    return {
      ...code,
      recipient: user ? {
        user_id: user.user_id,
        full_name: user.full_name,
        email: user.user_email,
        phone: user.user_phone_number,
        used_at: usage?.used_at
      } : null,
      business: business ? {
        business_id: business.business_id,
        business_name: business.business_name,
        contact_email: business.contact_email,
        phone: business.phone,
        business_status: business.business_status
      } : null,
      request: request ? {
        email: request.email,
        phone: request.phone,
        city: request.city,
        state: request.state,
        signup_intent: request.signup_intent
      } : null
    };
  });

  return { codes: enrichedCodes, total: count || 0 };
}

// Audit Logs
export async function getAuditLogs(
  page = 1,
  pageSize = 50,
  action?: string,
  entityType?: string
) {
  let query = supabase
    .from('admin_audit_logs')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (action) query = query.eq('action', action);
  if (entityType) query = query.eq('entity_type', entityType);

  const { data, count, error } = await query;
  if (error) throw error;

  return { logs: data || [], total: count || 0 };
}

// User Login Events (IP + Geolocation + Device tracking)
export async function getUserLoginEvents(
  page = 1,
  pageSize = 50,
  filters: UserLoginEventFilters = {}
): Promise<{ events: UserLoginEvent[]; total: number }> {
  let query = supabase
    .from('user_login_events')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (filters.country) query = query.eq('country_code', filters.country);
  if (filters.platform) query = query.eq('platform', filters.platform);
  if (filters.vpnOnly) query = query.eq('is_vpn', true);
  if (filters.startDate) query = query.gte('created_at', filters.startDate);
  if (filters.endDate) query = query.lte('created_at', filters.endDate);

  const { data, count, error } = await query;
  if (error) throw error;

  const events = (data || []) as UserLoginEvent[];

  // Enrich with user_profile info (name/email/phone) for display.
  // Done as a separate query because user_login_events.user_id references
  // auth.users(id), not user_profiles directly, so PostgREST can't auto-join.
  let enriched = events;
  if (events.length > 0) {
    const userIds = Array.from(new Set(events.map(e => e.user_id)));
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('user_id, full_name, email, phone_number')
      .in('user_id', userIds);

    const profileMap = new Map<string, { full_name?: string; email?: string; phone_number?: string }>(
      (profiles || []).map(p => [p.user_id, p])
    );

    enriched = events.map(event => {
      const profile = profileMap.get(event.user_id);
      return {
        ...event,
        user_full_name: profile?.full_name ?? null,
        user_email: profile?.email ?? null,
        user_phone: profile?.phone_number ?? null,
      };
    });
  }

  // Client-side user search (after enrichment so we can match name/email/phone)
  if (filters.userSearch) {
    const needle = filters.userSearch.toLowerCase();
    enriched = enriched.filter(e =>
      e.user_id.toLowerCase().includes(needle) ||
      (e.user_full_name?.toLowerCase().includes(needle) ?? false) ||
      (e.user_email?.toLowerCase().includes(needle) ?? false) ||
      (e.user_phone?.toLowerCase().includes(needle) ?? false) ||
      (e.ip_address?.toLowerCase().includes(needle) ?? false)
    );
  }

  return { events: enriched, total: count || 0 };
}

// Admin Actions (call edge functions)
export async function suspendUser(userId: string, reason: string, durationDays?: number) {
  return callEdgeFunction('admin-suspend-user', { userId, reason, durationDays });
}

export async function banUser(userId: string, reason: string) {
  return callEdgeFunction('admin-ban-user', { userId, reason });
}

export async function moderateBusiness(queueItemId: string, action: 'approve' | 'reject', notes?: string) {
  return callEdgeFunction('admin-moderate-business', { queueItemId, action, notes });
}

export async function refundPayment(paymentIntentId: string, reason: string, amount?: number) {
  return callEdgeFunction('admin-refund-payment', { paymentIntentId, reason, amount });
}

export async function resolveConversationFlag(
  flagId: string,
  resolution: string,
  resolutionNotes: string,
  actionTaken: string,
  tosSection?: string,
  suspensionDays?: number
) {
  return callEdgeFunction('admin-resolve-conversation-flag', {
    flagId,
    resolution,
    resolutionNotes,
    actionTaken,
    tosSection,
    suspensionDays,
  });
}

export async function resolveReviewFlag(
  flagId: string,
  resolution: string,
  resolutionNotes: string,
  actionTaken: string
) {
  return callEdgeFunction('admin-resolve-review-flag', {
    flagId,
    resolution,
    resolutionNotes,
    actionTaken,
  });
}

export async function issueViolation(
  userId: string,
  violationType: string,
  severity: string,
  notes: string,
  actionTaken: string,
  tosSection?: string,
  suspensionDays?: number
) {
  return callEdgeFunction('admin-issue-violation', {
    userId,
    violationType,
    severity,
    notes,
    actionTaken,
    tosSection,
    suspensionDays,
  });
}
